import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../config/api';
import { clearAllTripSnapshots } from '../utils/offlineTripStore';
import { clearOfflineApiCache } from '../utils/registerServiceWorker';
import {
  clearClientSessionBranding,
  persistClientSessionBranding,
} from './ClientBrandingContext';

const AuthContext = createContext();

const USER_STORAGE_KEY = 'user';

function normalizeUser(userData) {
  if (!userData) return null;
  return {
    id: userData.id,
    username: userData.username,
    email: userData.email,
    avatar: userData.avatar || '',
    bio: userData.bio || '',
    first_name: userData.first_name || '',
    last_name: userData.last_name || '',
    created_at: userData.created_at,
  };
}

function readCachedUser() {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) return null;
    return normalizeUser(JSON.parse(raw));
  } catch {
    return null;
  }
}

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const segment = token.split('.')[1];
  if (!segment) return null;
  const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
  return JSON.parse(atob(padded));
}

function userFromTokenClaims(token) {
  try {
    const payload = decodeJwtPayload(token);
    if (!payload?.id) return null;
    return normalizeUser({
      id: payload.id,
      username: payload.username,
      email: payload.email,
    });
  } catch {
    return null;
  }
}

function persistUserSession(userData) {
  const normalized = normalizeUser(userData);
  if (!normalized) return null;
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if token is expired client-side
  const isTokenExpired = (token) => {
    if (!token) return true;

    try {
      const payload = decodeJwtPayload(token);
      if (!payload?.exp) return true;
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Error checking token expiry:', error);
      return true;
    }
  };

  // Handle token expiry
  const handleTokenExpiry = () => {
    localStorage.removeItem('token');
    localStorage.removeItem(USER_STORAGE_KEY);
    clearClientSessionBranding();
    setUser(null);
    setError('Your session has expired. Please log in again.');
  };

  const applyAuthenticatedUser = useCallback((userData) => {
    const normalized = persistUserSession(userData);
    if (normalized) setUser(normalized);
    return normalized;
  }, []);

  /** Keep the traveler signed in when validate-token fails for non-auth reasons (common on flaky mobile networks). */
  const restoreSessionWithoutValidation = useCallback((token) => {
    const restored = readCachedUser() || userFromTokenClaims(token);
    if (restored) {
      setUser(restored);
      setError(null);
      return true;
    }
    return false;
  }, []);

  const validateToken = useCallback(async (token) => {
    try {
      const response = await api.get('/validate-token', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data && response.data.user) {
        persistClientSessionBranding(response.data.branding);
        applyAuthenticatedUser(response.data.user);
        setError(null);
      } else {
        console.error('AuthContext: Invalid user data received:', response.data);
        throw new Error('Invalid user data received');
      }
    } catch (error) {
      const status = error.response?.status;
      // Only hard-logout on explicit auth rejection. 404/5xx/network must not
      // wipe a still-valid year-long mobile session on cold start.
      const authFailure = status === 401 || status === 403;
      if (authFailure) {
        console.error('AuthContext: Token validation error:', error);
        handleTokenExpiry();
      } else {
        console.error('AuthContext: Token validation request failed; keeping session:', error);
        restoreSessionWithoutValidation(token);
      }
    } finally {
      setLoading(false);
    }
  }, [applyAuthenticatedUser, restoreSessionWithoutValidation]);

  useEffect(() => {
    // Listen for token expiry events from API interceptor
    const handleTokenExpiredEvent = () => {
      handleTokenExpiry();
    };

    window.addEventListener('auth:token-expired', handleTokenExpiredEvent);

    // Check for existing token on app load
    const token = localStorage.getItem('token');
    
    if (token) {
      // Check if token is expired client-side first
      if (isTokenExpired(token)) {
        handleTokenExpiry();
        setLoading(false);
      } else {
        // Hydrate immediately from cache/JWT so flaky mobile networks don't
        // bounce travelers to /login while validate-token is in flight.
        restoreSessionWithoutValidation(token);
        validateToken(token);
      }
    } else {
      setLoading(false);
    }

    // Cleanup event listener
    return () => {
      window.removeEventListener('auth:token-expired', handleTokenExpiredEvent);
    };
  }, [validateToken, restoreSessionWithoutValidation]);

  const login = async (identifier, password, inviteId, brandToken) => {
    try {
      setError(null);
      const response = await api.post('/auth/unified-login', { 
        email: identifier, 
        password, 
        invite: inviteId,
        ...(brandToken ? { brand: brandToken } : {}),
      });
      
      if (!response.data || !response.data.token) {
        throw new Error('Invalid response from server');
      }

      const { token, accountType, user, partner } = response.data;
      
      if (accountType === 'partner') {
        // Partner login
        localStorage.setItem('partnerToken', token);
        localStorage.setItem('partner', JSON.stringify(partner));
        return { success: true, accountType: 'partner' };
      } else {
        // User login
        if (!user) {
          throw new Error('Invalid user data from server');
        }
        persistClientSessionBranding(response.data.branding);
        localStorage.setItem('token', token);
        applyAuthenticatedUser(user);
        return { success: true, accountType: 'user' };
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.error || 'Login failed';
      const requiresVerification = error.response?.data?.requiresVerification || false;
      const unverifiedEmail = error.response?.data?.email || '';
      setError(errorMessage);
      return { success: false, error: errorMessage, requiresVerification, email: unverifiedEmail };
    }
  };

  const socialLogin = async (provider, authToken, tokenType = 'idToken') => {
    try {
      setError(null);
      const body = { provider, tokenType };
      if (tokenType === 'accessToken') {
        body.accessToken = authToken;
      } else {
        body.idToken = authToken;
      }
      const brandToken = new URLSearchParams(window.location.search).get('brand');
      if (brandToken) body.brand = brandToken;
      const response = await api.post('/auth/social-login', body, { timeout: 60000 });

      if (!response.data || !response.data.token) {
        throw new Error('Invalid response from server');
      }

      const { token: jwtToken, user } = response.data;

      if (!user) {
        throw new Error('Invalid user data from server');
      }

      persistClientSessionBranding(response.data.branding);
      localStorage.setItem('token', jwtToken);
      applyAuthenticatedUser(user);

      return { success: true, accountType: 'user', isNewUser: response.data.isNewUser };
    } catch (error) {
      console.error('Social login error:', error);
      const errorMessage = error.response?.data?.error || 'Social login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const googleCodeLogin = async (code, redirectUri) => {
    try {
      setError(null);
      const brandToken = new URLSearchParams(window.location.search).get('brand');
      const response = await api.post('/auth/google-code-login', {
        code,
        redirect_uri: redirectUri,
        ...(brandToken ? { brand: brandToken } : {}),
      }, { timeout: 60000 });

      if (!response.data || !response.data.token) {
        throw new Error('Invalid response from server');
      }

      const { token: jwtToken, user: userData } = response.data;

      if (!userData) {
        throw new Error('Invalid user data from server');
      }

      persistClientSessionBranding(response.data.branding);
      localStorage.setItem('token', jwtToken);
      applyAuthenticatedUser(userData);

      return { success: true, accountType: 'user', isNewUser: response.data.isNewUser };
    } catch (error) {
      console.error('Google code login error:', error);
      const errorMessage = error.response?.data?.error || 'Google sign-in failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const loginWithCredentials = (token, userData) => {
    localStorage.setItem('token', token);
    applyAuthenticatedUser(userData);
    setError(null);
  };

  const logout = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await api.post('/auth/logout', {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        // Still clear local session even if revoke fails (offline, etc.)
        console.error('Server logout failed:', error?.message || error);
      }
    }
    localStorage.removeItem('token');
    localStorage.removeItem(USER_STORAGE_KEY);
    clearClientSessionBranding();
    // Trips saved for offline reading hold hotel addresses and confirmation
    // numbers, so they leave with the session rather than waiting on the device
    // for whoever signs in next.
    clearAllTripSnapshots().catch(() => {});
    clearOfflineApiCache();
    setUser(null);
    setError(null);
  };

  const updateUser = (userData) => {
    if (!userData) return;

    setUser((prev) => {
      const merged = normalizeUser({
        ...(prev || {}),
        ...userData,
        avatar: userData.avatar || prev?.avatar || '',
        bio: userData.bio || prev?.bio || '',
        first_name: userData.first_name ?? prev?.first_name ?? '',
        last_name: userData.last_name ?? prev?.last_name ?? '',
        created_at: userData.created_at || prev?.created_at,
      });
      if (merged) {
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(merged));
      }
      return merged;
    });
  };

  const updatePreferences = async (preferences) => {
    try {
      setError(null);
      
      // Check token expiry before making the request
      const token = localStorage.getItem('token');
      if (!token || isTokenExpired(token)) {
        handleTokenExpiry();
        return { success: false, error: 'Session expired. Please log in again.' };
      }

      await api.put('/preferences', { preferences });
      return { success: true };
    } catch (error) {
      console.error('Error updating preferences:', error);
      setError(error.response?.data?.error || 'Failed to update preferences');
      return { success: false, error: error.response?.data?.error || 'Failed to update preferences' };
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    socialLogin,
    googleCodeLogin,
    loginWithCredentials,
    logout,
    updateUser,
    updatePreferences,
    isTokenExpired
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 